// Wheel segment CONTRAST GUARD — render-time hairline safety net.
// --------------------------------------------------------------------------
// PROBLEM (Gordon 2026-07-20): a white / near-white segment on a white / light
// surface reads as a BLANK slice — the segment is there but its boundary against
// the backdrop (and against an adjacent light segment) is imperceptible. First
// seen in the app-download popup's wheel; ruled to apply to real Wheel renders.
//
// FIX (this module + createPie.js): at render time, compute each segment fill's
// luminance-contrast against the effective backdrop. When contrast FAILS a
// stated threshold, draw a 1px hairline boundary stroke on THAT segment only,
// coloured as the segment's OWN fill shaded by a fixed perceptual delta (never a
// generic gray). Segments with adequate contrast get ZERO change — byte-
// identical render (the fidelity guarantee). We NEVER recolour a fill.
//
// This module owns the PURE logic + the build-time flag. createPie.js consumes
// it. Flag convention mirrors segmentIconConfig.js / constants/featureFlags.js:
// a REACT_APP_* build var, default OFF, so OFF == today, byte-identical.
//
// NOTE: there is no pre-existing WCAG_CONTRAST_GUARD / SEGMENT_PALETTE_* flag in
// the codebase (grep-verified). The palette hooks (useSegmentPalettes /
// usePaletteColor) generate/resolve colors but do NOT prevent a user-chosen or
// imported white fill; this guard is the RENDER-time net regardless of where the
// white came from.

const truthy = (v) => v === "true" || v === "1" || v === true;

// SEGMENT_HAIRLINE ENABLED — when ON, createPie.js draws the conditional hairline
// on low-contrast segments. When OFF (default) the wheel renders exactly as today.
export const SEGMENT_HAIRLINE_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_SEGMENT_HAIRLINE
);

// --------------------------------------------------------------------------
// Tunable constants (not magic numbers).
//
// NEAR_WHITE_CHANNEL_DELTA — the maximum per-channel (0..255) difference from the
// backdrop for a fill to count as "indistinguishable" and earn a hairline. 12 ≈
// 4.7% of 255 = Gordon's "within a few % of #FFFFFF" (2026-07-21). Tuned so ONLY
// near-PURE-white fires against a white rim ("WHITE WHITE WHITE and maybe a few
// degrees away"): #fafafa / #f5f5f5 fire; #f0f0f0, cream #fff8dc, light-blue
// #add8e6, light-green #90ee90, tan #d2b48c and light-gray #e1e1e1 do NOT — each
// differs from white by more than 12 on at least one channel, so it stays clearly
// distinct. This REPLACES the old luminance-only 1.5:1 contrast bound, which
// fired far too liberally on any high-luminance colour regardless of hue (cream,
// pale blue/green, tan all tripped it). A per-channel distance captures BOTH
// "very high luminance AND very low chroma" in a single symmetric metric: a
// near-black fill on a dark scene still fires, since it is near its backdrop too.
export const NEAR_WHITE_CHANNEL_DELTA = 12;

// HAIRLINE_STROKE_WIDTH — px width of the boundary stroke. A true hairline.
export const HAIRLINE_STROKE_WIDTH = 1;

// HAIRLINE_SHADE_PERCENT — fixed perceptual delta applied to the fill to derive
// the stroke colour (darken on a light backdrop, lighten on a dark one).
export const HAIRLINE_SHADE_PERCENT = 32;

// DARK_BACKDROP_LUMINANCE — a backdrop at/above this relative luminance is
// treated as "light" (darken the stroke); below it is "dark" (lighten the stroke).
export const DARK_BACKDROP_LUMINANCE = 0.5;

// DEFAULT_BACKDROP — assumed surface when none can be resolved. The bug case
// (and the overwhelmingly common surface) is a white page/dialog, so default
// white: conservative (a white segment on an unknown surface still gets rescued).
export const DEFAULT_BACKDROP = "#ffffff";

// --------------------------------------------------------------------------
// Colour parsing. Accepts #rgb, #rrggbb, rgb()/rgba(), and the two CSS keywords a
// DOM backdrop walk actually produces (white, transparent). Returns {r,g,b} in
// 0..255, or null when the value is not an opaque parseable colour (e.g.
// "transparent", "url(...)", a pattern fill, or garbage).
export const parseColor = (input) => {
  if (typeof input !== "string") return null;
  const v = input.trim().toLowerCase();
  if (!v || v === "transparent" || v === "none") return null;
  if (v === "white") return { r: 255, g: 255, b: 255 };
  if (v === "black") return { r: 0, g: 0, b: 0 };

  // #rgb / #rrggbb
  if (v[0] === "#") {
    let hex = v.slice(1);
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6 || /[^0-9a-f]/.test(hex)) return null;
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  // rgb() / rgba() — treat alpha 0 as non-opaque (null).
  const m = v.match(
    /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?\s*\)$/
  );
  if (m) {
    const a = m[4] === undefined ? 1 : parseFloat(m[4]);
    if (a === 0) return null;
    return {
      r: Math.min(255, Math.round(parseFloat(m[1]))),
      g: Math.min(255, Math.round(parseFloat(m[2]))),
      b: Math.min(255, Math.round(parseFloat(m[3]))),
    };
  }
  return null;
};

const toHex = ({ r, g, b }) => {
  const h = (n) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return "#" + h(r) + h(g) + h(b);
};

// sRGB 8-bit channel -> linear light (WCAG 2.x formula).
const channelToLinear = (c8) => {
  const c = c8 / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

// WCAG relative luminance 0..1, or null if unparseable.
export const relativeLuminance = (input) => {
  const rgb = parseColor(input);
  if (!rgb) return null;
  return (
    0.2126 * channelToLinear(rgb.r) +
    0.7152 * channelToLinear(rgb.g) +
    0.0722 * channelToLinear(rgb.b)
  );
};

// WCAG contrast ratio 1..21 between two colours, or null if either is unparseable.
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
};

// Is a backdrop colour "dark" (so the stroke should be LIGHTENED, not darkened)?
export const isDarkBackdrop = (backdrop) => {
  const l = relativeLuminance(backdrop);
  if (l === null) return false; // unknown -> treat as light (darken)
  return l < DARK_BACKDROP_LUMINANCE;
};

// Does this fill need a hairline against this backdrop? True ONLY when the fill
// is so close to the backdrop on EVERY colour channel that the two are visually
// indistinguishable — the reported "blank slice" of a near-white segment on the
// white rim (Gordon 2026-07-21). A per-channel distance ≤ NEAR_WHITE_CHANNEL_DELTA
// demands BOTH high luminance similarity AND low chroma difference, so cream /
// tan / pastel / light-gray — clearly distinct from white — never trigger, while
// near-pure-white (and, symmetrically, near-black on a dark scene) still does. A
// pattern / image / unparseable fill or backdrop returns false (nothing to
// compute; imagery has its own texture).
export const needsHairline = (
  fill,
  backdrop,
  delta = NEAR_WHITE_CHANNEL_DELTA
) => {
  const f = parseColor(fill);
  const b = parseColor(backdrop);
  if (!f || !b) return false;
  return (
    Math.abs(f.r - b.r) <= delta &&
    Math.abs(f.g - b.g) <= delta &&
    Math.abs(f.b - b.b) <= delta
  );
};

// Derive the hairline stroke colour from the segment's OWN fill: darken on a
// light backdrop, lighten on a dark one, by a fixed perceptual delta. Returns a
// hex string, or null if the fill is unparseable (caller then draws no stroke).
export const deriveHairlineStroke = (
  fill,
  backdropDark,
  shadePercent = HAIRLINE_SHADE_PERCENT
) => {
  const rgb = parseColor(fill);
  if (!rgb) return null;
  const delta = (shadePercent / 100) * 255;
  if (backdropDark) {
    // lighten toward white
    return toHex({ r: rgb.r + delta, g: rgb.g + delta, b: rgb.b + delta });
  }
  // darken toward black (multiplicative keeps hue proportions)
  const f = 1 - shadePercent / 100;
  return toHex({ r: rgb.r * f, g: rgb.g * f, b: rgb.b * f });
};

// Inset a wedge's outline IN from its white divider stroke so a hairline drawn on
// the result traces where the segment's COLOUR meets the white rim / inter-wedge
// border — never the wheel's outer rim (Gordon 2026-07-21: a light segment must
// not look larger than a dark one). The base render strokes EVERY wedge with a
// white divider of width `dividerStrokeWidth` straddling the arc boundary, so the
// visible colour is inset by dividerStrokeWidth/2 on every edge; the hairline
// path matches that exact boundary, and — because the base white divider is left
// untouched — light and dark segments keep an identical visible size. Pure
// geometry (no d3, no DOM) so BOTH renderers (createPie's d3.arc + renderer-v2's
// wedgePath) consume one definition and stay in lock-step. The radial sides are
// inset by the arc-length equivalent of the radial inset taken at the outer
// radius, clamped so a razor-thin wedge never inverts (start stays < end).
export const insetWedgeForHairline = (
  { innerRadius, outerRadius, startAngle, endAngle },
  dividerStrokeWidth = 0
) => {
  const inset = Math.max(0, dividerStrokeWidth / 2);
  const outer = Math.max(0, outerRadius - inset);
  const inner = Math.min(outer, Math.max(0, innerRadius) + inset);
  const angularInset = outer > 0 ? inset / outer : 0;
  const maxInset = Math.max(0, (endAngle - startAngle) / 2 - 1e-4);
  const a = Math.min(angularInset, maxInset);
  return {
    innerRadius: inner,
    outerRadius: outer,
    startAngle: startAngle + a,
    endAngle: endAngle - a,
  };
};

// Resolve the enable decision. Production callers never pass config.segmentHairline
// so this collapses to the build-time flag (OFF by default -> byte-identical).
// Fixtures/tests pass config.segmentHairline to toggle without a rebuild.
export const isSegmentHairlineEnabled = (config) => {
  const override = config && config.segmentHairline;
  return override === undefined || override === null
    ? SEGMENT_HAIRLINE_ENABLED
    : truthy(override);
};

// ==========================================================================
// LABEL LEGIBILITY SYSTEM (Fable audit A2/W3, coordinator scope-extension).
// Same guard family, its OWN sub-flag so it can be toggled / rolled back
// independently of the arc hairline. Two problems, same never-recolour-the-
// SEGMENT law — we only ever adjust the LABEL:
//   (a) contrast: a label whose text colour fails WCAG AA vs its OWN segment
//       fill (audit: 6/10 labels fail, worst white-on-mid-purple 2.61:1) is
//       switched to the higher-contrast of {white, ink} — only when failing, so
//       an already-passing label is byte-identical.
//   (b) truncation: the flat 10-char cap truncates labels while arc space sits
//       unused; size the char budget to the slice's actual arc length so wider
//       slices show more, ellipsis only when genuinely out of room. Never shows
//       FEWER chars than the legacy floor.
// ==========================================================================

// SEGMENT_LABEL_LEGIBILITY ENABLED — build-time sub-flag, default OFF.
export const SEGMENT_LABEL_LEGIBILITY_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_LABEL_LEGIBILITY
);

// WCAG 2.x §1.4.3 AA for normal (<18pt / <14pt bold) text is 4.5:1. Wheel labels
// render at ~12px, so 4.5 is the correct bar (distinct from the arc hairline's
// 1.5 graphical-boundary threshold — a label is TEXT, held to the stricter rule).
export const LABEL_CONTRAST_THRESHOLD = 4.5;

// The two candidate label inks. LABEL_INK is the brand canonical ink (matches
// pillColors.PILL_INK). A failing label switches to whichever contrasts more.
export const LABEL_INK = "#123051";
export const LABEL_LIGHT = "#ffffff";

// Adaptive-truncation tunables.
export const LABEL_MAX_CHARS = 40;
export const LABEL_CHAR_WIDTH_RATIO = 0.55; // avg glyph advance as a fraction of font size

// Choose the label text colour against its own segment-fill background.
// Returns `current` UNCHANGED when it already meets the threshold (byte-identical
// for passing labels); otherwise the higher-contrast of {light, ink}. If the
// background is unparseable, returns `current` (nothing to compute).
export const bestLabelTextColor = (
  bgColor,
  current,
  {
    threshold = LABEL_CONTRAST_THRESHOLD,
    ink = LABEL_INK,
    light = LABEL_LIGHT,
  } = {}
) => {
  if (!parseColor(bgColor)) return current;
  const currentRatio = contrastRatio(current, bgColor);
  if (currentRatio !== null && currentRatio >= threshold) return current;
  const inkRatio = contrastRatio(ink, bgColor) || 0;
  const lightRatio = contrastRatio(light, bgColor) || 0;
  return inkRatio >= lightRatio ? ink : light;
};

// Adaptive character budget for a label, from the slice's tangential arc length
// at the label radius. Never returns fewer than `floorChars` (the legacy cap),
// so enabling this can only ever REVEAL more text, never truncate harder.
export const computeLabelCharLimit = (
  arcAngleRad,
  labelRadiusPx,
  fontSizePx,
  floorChars,
  { maxChars = LABEL_MAX_CHARS, charWidthRatio = LABEL_CHAR_WIDTH_RATIO } = {}
) => {
  const floor = Math.max(1, Math.round(floorChars || 1));
  const angle = Number(arcAngleRad);
  const r = Number(labelRadiusPx);
  const fs = Number(fontSizePx);
  if (!(angle > 0) || !(r > 0) || !(fs > 0)) return floor;
  const arcLength = angle * r;
  const charWidth = fs * charWidthRatio;
  const fit = Math.floor(arcLength / charWidth);
  return Math.max(floor, Math.min(maxChars, fit));
};

// COMPB-02: computeLabelCharLimit's arc-length budget has no idea where the
// SVG canvas boundary actually is — it can only ever REVEAL more text, never
// truncate harder — so a label positioned near the rim can still produce a
// pill wide enough to bleed past the canvas edge before anything truncates
// it. Clamp the char budget separately against the actual pixel room left
// between the label's anchor and the canvas edge, using the SAME avg-glyph-
// width heuristic. Never returns fewer than 1 char (an ellipsis-only pill
// beats a missing one), and never widens `chars` beyond what it already was.
export const clampCharsToAvailableWidth = (
  chars,
  availableWidthPx,
  fontSizePx,
  { charWidthRatio = LABEL_CHAR_WIDTH_RATIO } = {}
) => {
  const budget = Math.max(1, Math.round(chars || 1));
  const width = Number(availableWidthPx);
  const fs = Number(fontSizePx);
  if (!(width > 0) || !(fs > 0)) return budget;
  const charWidth = fs * charWidthRatio;
  const maxCharsForWidth = Math.floor(width / charWidth);
  return Math.max(1, Math.min(budget, maxCharsForWidth));
};

// Enable seam — mirrors isSegmentHairlineEnabled. Production leaves
// config.segmentLabelLegibility undefined ⇒ the env sub-flag governs (OFF).
export const isLabelLegibilityEnabled = (config) => {
  const override = config && config.segmentLabelLegibility;
  return override === undefined || override === null
    ? SEGMENT_LABEL_LEGIBILITY_ENABLED
    : truthy(override);
};

// Walk up the DOM from `element` to the first ancestor with a non-transparent
// background-color; return it as a colour string. Falls back to DEFAULT_BACKDROP.
// A thin, DOM-touching wrapper around the pure logic above (kept out of the unit
// tests, which cover the pure functions). `getStyle` is injectable for testing.
export const resolveEffectiveBackdrop = (
  element,
  fallback = DEFAULT_BACKDROP,
  getStyle = typeof window !== "undefined" && window.getComputedStyle
    ? (el) => window.getComputedStyle(el)
    : null
) => {
  try {
    let el = element;
    let guard = 0;
    while (el && el.nodeType === 1 && guard < 50 && getStyle) {
      const bg = getStyle(el).backgroundColor;
      if (parseColor(bg)) return bg;
      el = el.parentElement;
      guard += 1;
    }
  } catch (e) {
    /* fall through to fallback */
  }
  return fallback;
};
